export type SidebarMorph = {
  default: number
  _default: number
  morphId: number
  order: number
  type: string
  min: number
  max: number
  name: string
  show: boolean
  modifyable: boolean
  reversed?: boolean
}

type MorphLike = Partial<SidebarMorph> & {
  nombre?: string
  valor?: number
  valorMin?: number
  valorMax?: number
  idMorph?: number
  orden?: number
  tipo?: string
  reversed?: boolean
}

function asNumber(value: unknown, fallback = 0): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function normalizeMorph(raw: MorphLike, index: number): SidebarMorph | null {
  const name = String(raw.name ?? raw.nombre ?? '').trim()
  if (!name) return null
  const value = asNumber(raw.default ?? raw.valor, 0)
  const min = asNumber(raw.min ?? raw.valorMin, value)
  const max = asNumber(raw.max ?? raw.valorMax, value)
  return {
    default: value,
    _default: asNumber(raw._default, value),
    morphId: asNumber(raw.morphId ?? raw.idMorph, index),
    order: asNumber(raw.order ?? raw.orden, index + 1),
    type: String(raw.type ?? raw.tipo ?? ''),
    min,
    max,
    name,
    show: raw.show !== false,
    modifyable: raw.modifyable !== false,
    reversed: raw.reversed,
  }
}

/** La unidad tiene regleta de catálogo (min < max). KBRB80932 / L 2 puertas: todo null. */
export function itemHasRegletaControl(item: {
  regleta?: number | null
  regletaMinimo?: number | null
  regletaMaximo?: number | null
} | null | undefined): boolean {
  if (!item) return false
  const min = Number(item.regletaMinimo)
  const max = Number(item.regletaMaximo)
  return Number.isFinite(min) && Number.isFinite(max) && max > min
}

function dimMorph(
  name: 'Ancho' | 'Fondo' | 'Alto',
  value: number,
  min?: number,
  max?: number,
  modifyable = true,
): SidebarMorph {
  const v = asNumber(value, 0)
  const lo = asNumber(min, v)
  const hi = asNumber(max, v)
  return {
    default: v,
    _default: v,
    morphId: name === 'Ancho' ? 1 : name === 'Fondo' ? 2 : 3,
    order: name === 'Ancho' ? 1 : name === 'Fondo' ? 2 : 3,
    type: name === 'Ancho' ? 'A' : name === 'Fondo' ? 'F' : 'T',
    min: lo,
    max: hi,
    name,
    show: true,
    modifyable,
  }
}

/**
 * Morphs listos para la sidebar.
 * Si faltan o vienen mal formados, los reconstruye — crítico para KBR*.
 */
export function ensureSidebarMorphs(item: any): SidebarMorph[] {
  if (!item) return []

  const raw = Array.isArray(item.morphs) ? item.morphs : []
  const normalized = raw
    .map((m: MorphLike, i: number) => normalizeMorph(m, i))
    .filter((m: SidebarMorph | null): m is SidebarMorph => Boolean(m))

  const byName = new Map(normalized.map((m) => [m.name, m]))

  const width = asNumber(item.width, asNumber(byName.get('Ancho')?.default, 0))
  const thickness = asNumber(item.thickness, asNumber(byName.get('Fondo')?.default, 0))
  const height = asNumber(item.height, asNumber(byName.get('Alto')?.default, 0))

  // Sin morphs de malla (p.ej. KBRB8010040): dims fijas; Regleta solo si el catálogo la trae.
  if (!byName.has('Ancho')) {
    byName.set(
      'Ancho',
      dimMorph('Ancho', width, width, width, Boolean(item.modificarAncho) || Boolean(item.hasMorphs)),
    )
  }
  if (!byName.has('Fondo')) {
    byName.set(
      'Fondo',
      dimMorph(
        'Fondo',
        thickness,
        thickness,
        thickness,
        Boolean(item.modificarFondo) || Boolean(item.hasMorphs),
      ),
    )
  }
  if (!byName.has('Alto')) {
    byName.set(
      'Alto',
      dimMorph('Alto', height, height, height, Boolean(item.modificarAlto) || Boolean(item.hasMorphs)),
    )
  }

  if (itemHasRegletaControl(item)) {
    const regleta = asNumber(item.regleta, asNumber(item.regletaMinimo, 0))
    const regletaMin = asNumber(item.regletaMinimo, 0)
    const regletaMax = asNumber(item.regletaMaximo, 0)
    const existing = byName.get('Regleta')
    const existingRangeOk =
      existing != null && Number(existing.min) < Number(existing.max)
    // Valor API/sidebar directo. La inversión GLB (reposo=max) está en getRegletaMorphInfluence.
    byName.set('Regleta', {
      default: typeof item.regleta === 'number' ? regleta : (existing?.default ?? regleta),
      _default: typeof item.regleta === 'number' ? regleta : (existing?._default ?? regleta),
      morphId: existing?.morphId ?? -1,
      order: 999,
      type: 'R',
      min: existingRangeOk ? Number(existing.min) : regletaMin,
      max: existingRangeOk ? Number(existing.max) : regletaMax,
      name: 'Regleta',
      show: true,
      modifyable: true,
      reversed: false,
    })
  } else {
    byName.delete('Regleta')
  }

  const orderRank = (name: string) => {
    if (name === 'Ancho') return 1
    if (name === 'Fondo') return 2
    if (name === 'Alto') return 3
    if (name === 'Regleta') return 999
    return 50
  }

  return Array.from(byName.values()).sort(
    (a, b) => orderRank(a.name) - orderRank(b.name) || a.order - b.order,
  )
}
